import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../loader/Loader";
import ModalComponent from "../modalComponent/ModalComponent";
import { closeModal, openModal } from "../modalComponent/modalSlice";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import toast from "react-hot-toast";
import BankService from "../../services/BankService";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

// Define Yup schema for validation
const bankDetailsSchema = Yup.object().shape({
  sortCode: Yup.string()
    .required("Please enter a Sort Code.")
    .matches(
      /^\d{2}-\d{2}-\d{2}$/,
      "Sort Code must be in the format 00-00-00."
    ),
  accountNo: Yup.string()
    .required("Please enter an Account Number.")
    .matches(/^\d{8}$/, "Account Number must be exactly 8 digits."),
  accountHolderName: Yup.string().required("Account Holder Name is required."),
});

const BankDetails = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showSortCode, setShowSortCode] = useState(false);
  const [showAccountNo, setShowAccountNo] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { encryptedCreditCheckId, encryptedUserId } = useParams();
  const modalInitState = useSelector((state) => state.modal);
  const didRunCheckRef = React.useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    trigger,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(bankDetailsSchema),
    mode: "onBlur",
    defaultValues: {
      sortCode: "",
      accountNo: "",
      accountHolderName: "",
    },
  });

  // Log modal state changes for debugging
  useEffect(() => {
    console.log("Modal contentId changed:", modalInitState.contentId);
  }, [modalInitState.contentId]);

  const handletoggleSortCode = () => {
    setShowSortCode(!showSortCode);
  };

  const handletoggleAccountNo = () => {
    setShowAccountNo(!showAccountNo);
  };

  // Handle errors consistently
  const handleError = useCallback(
    (error) => {
      console.error("Handling error:", {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
      });
      setIsLoading(false);
      setPendingFormData(null);
      let errorMessage = "Unknown error occurred. Please try again later.";
      let modalType = "unknown error";

      if (error.message === "Network Error") {
        errorMessage = "Network Error Occurred. Please try again later.";
        modalType = "network error";
      } else if (error.response) {
        switch (error.response.status) {
          case 401:
          case 403:
          case 404:
          case 406:
          case 412:
            errorMessage =
              error.response.data?.failedReason ||
              error.response.data?.message ||
              "Request failed.";
            modalType = "error occurred";
            break;
          case 500:
            errorMessage = "Server error occurred. Please try again later.";
            modalType = "server error";
            break;
          default:
            modalType = "unknown error";
            break;
        }
      }
      setMessage(errorMessage);
      // Only dispatch error modal if submission hasn't occurred
      if (!isSubmitted) {
        dispatch(openModal(modalType));
      }
    },
    [dispatch, isSubmitted]
  );

  // Check bank details and link validity
  useEffect(() => {
    if (didRunCheckRef.current || isSubmitted) return;
    didRunCheckRef.current = true;

    if (!encryptedCreditCheckId || !encryptedUserId) {
      setIsLoading(false);
      setMessage("Invalid or missing IDs in the link");
      dispatch(openModal("error occurred"));
      return;
    }

    setIsLoading(true);

    BankService.checkBankDetailsSubmitted(
      encryptedCreditCheckId,
      encryptedUserId,
      1
    )
      .then((response) => {
        console.log("checkBankDetailsSubmitted response:", response);
        if (!response.data) {
          throw new Error("No data returned from checkBankDetailsSubmitted");
        }

        if (response.status === 200 && response.data.isSubmitted) {
          setMessage(
            "Bank details have already been submitted and cannot be changed."
          );
          dispatch(openModal("already submitted"));
          setIsSubmitted(true);
          setShowForm(false);
          setIsLoading(false);
        } else {
          BankService.checkLinkValidity(encryptedCreditCheckId, encryptedUserId)
            .then((linkResponse) => {
              console.log("checkLinkValidity response:", linkResponse);
              setIsLoading(false);
              if (!linkResponse.data) {
                throw new Error("No data returned from checkLinkValidity");
              }

              if (
                linkResponse.status === 200 &&
                linkResponse.data.status === "success"
              ) {
                setShowForm(true);
                setValue("accountHolderName", linkResponse.data.data || "");
              } else {
                setMessage(
                  linkResponse.data.message ||
                    "This link has expired. Please request a new link."
                );
                dispatch(openModal("link expired"));
                setShowForm(false);
              }
            })
            .catch(handleError);
        }
      })
      .catch(handleError);
  }, [
    encryptedCreditCheckId,
    encryptedUserId,
    dispatch,
    setValue,
    handleError,
    isSubmitted,
  ]);

  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, "");
    const limitedDigits = digits.slice(0, 6);
    let formatted = "";
    for (let i = 0; i < limitedDigits.length; i++) {
      if (i > 0 && i % 2 === 0 && i < 6) {
        formatted += "-";
      }
      formatted += limitedDigits[i];
    }
    return formatted;
  };

  const onSubmit = async (data) => {
    setPendingFormData(data);
    dispatch(openModal("confirmSubmission"));
  };

  const handleConfirmSubmission = async () => {
    if (!pendingFormData) return;

    setIsLoading(true);
    setMessage("");
    try {
      const response = await BankService.submitBankDetails({
        encryptedCreditCheckId: encodeURIComponent(encryptedCreditCheckId),
        encryptedUserId: encodeURIComponent(encryptedUserId),
        bicCode: pendingFormData.sortCode,
        ibanNumber: pendingFormData.accountNo,
      });
      console.log("submitBankDetails response:", response);

      if (response.status === 200) {
        setIsSubmitted(true);
        setShowForm(false);
        setIsLoading(false);
        setPendingFormData(null);
        dispatch(closeModal());
        dispatch(openModal("submission success"));
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-start px-6 py-3 lg:px-8">
        {showForm && (
          <div className="mx-auto w-full max-w-sm md:max-w-md">
            <div className="flex flex-col justify-center items-center">
              <h2 className="text-center text-2xl font-semibold leading-9 tracking-tight primary-text">
                Enter Bank Details
              </h2>
            </div>
            <div className="mt-2">
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label
                    htmlFor="accountHolderName"
                    className="block text-sm font-normal leading-6"
                  >
                    Account Holder Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="accountHolderName"
                      {...register("accountHolderName")}
                      type="text"
                      disabled
                      className="block w-full rounded-md border custom-border-50 bg-gray-100 text-gray-500 px-3 py-1.5 sm:text-sm sm:leading-6 cursor-not-allowed"
                    />
                  </div>
                  {errors.accountHolderName && (
                    <p className="error-text">
                      {errors.accountHolderName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="sortCode"
                    className="block text-sm font-normal leading-6"
                  >
                    Sort Code*
                  </label>
                  <div className="mt-2 relative">
                    <Controller
                      name="sortCode"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="sortCode"
                          type={showSortCode ? "text" : "password"}
                          placeholder="e.g., 12-34-56"
                          value={field.value}
                          onChange={(e) => {
                            const formattedValue = formatSortCode(
                              e.target.value
                            );
                            field.onChange(formattedValue);
                            trigger("sortCode");
                          }}
                          onBlur={() => trigger("sortCode")}
                          className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 pr-10 ${
                            errors.sortCode && touchedFields.sortCode
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                      )}
                    />
                    <div
                      onClick={handletoggleSortCode}
                      className="absolute top-1/2 -translate-y-1/2 right-2 w-[18px] cursor-pointer"
                    >
                      {showSortCode ? <EyeSlashIcon /> : <EyeIcon />}
                    </div>
                  </div>
                  {errors.sortCode && (
                    <p className="error-text">{errors.sortCode.message}</p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="accountNo"
                    className="block text-sm font-normal leading-6"
                  >
                    Account Number*
                  </label>
                  <div className="mt-2 relative">
                    <input
                      id="accountNo"
                      {...register("accountNo")}
                      type={showAccountNo ? "text" : "password"}
                      placeholder="e.g., 12345678"
                      className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 pr-10 ${
                        errors.accountNo && touchedFields.accountNo
                          ? "border-red-500"
                          : ""
                      }`}
                      onBlur={() => trigger("accountNo")}
                    />
                    <div
                      onClick={handletoggleAccountNo}
                      className="absolute top-1/2 -translate-y-1/2 right-2 w-[18px] cursor-pointer"
                    >
                      {showAccountNo ? <EyeSlashIcon /> : <EyeIcon />}
                    </div>
                  </div>
                  {errors.accountNo && (
                    <p className="error-text">{errors.accountNo.message}</p>
                  )}
                </div>
                {message && <p className="error-text">{message}</p>}
                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md cta-one px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                    disabled={isLoading}
                  >
                    Submit
                  </button>
                  {isLoading && <p>Loading...</p>}
                </div>
              </form>
            </div>
          </div>
        )}

        {modalInitState.contentId && (
          <ModalComponent
            size="md"
            type={
              modalInitState.contentId === "submission success" ||
              modalInitState.contentId === "already submitted" ||
              modalInitState.contentId === "confirmSubmission"
                ? "success"
                : "error"
            }
            crossButton={
              !(
                modalInitState.contentId === "submission success" ||
                modalInitState.contentId === "already submitted" ||
                modalInitState.contentId === "confirmSubmission"
              )
            }
            position="center"
            title={
              modalInitState.contentId === "network error"
                ? "Network Error"
                : modalInitState.contentId === "server error"
                ? "Server Error"
                : modalInitState.contentId === "unknown error"
                ? "Unknown Error"
                : modalInitState.contentId === "not found"
                ? "Not Found"
                : modalInitState.contentId === "link expired"
                ? "Link Expired"
                : modalInitState.contentId === "already submitted"
                ? "Already Submitted"
                : modalInitState.contentId === "submission success"
                ? "Submission Successful"
                : modalInitState.contentId === "confirmSubmission"
                ? "Confirm Bank Details Submission"
                : "Error"
            }
            description=""
            bottomLine={true}
          >
            <div>
              {modalInitState.contentId === "network error" ? (
                <p>Network Error Occurred. Please try again later.</p>
              ) : modalInitState.contentId === "server error" ? (
                <p>Server Error Occurred. Please try again later.</p>
              ) : modalInitState.contentId === "unknown error" ? (
                <p>Unknown Error Occurred. Please try again later.</p>
              ) : modalInitState.contentId === "not found" ? (
                <p>Request not found. Please check the link and try again!</p>
              ) : modalInitState.contentId === "link expired" ? (
                <p>{message}</p>
              ) : modalInitState.contentId === "error occurred" ? (
                <div className="flex flex-col space-y-4">
                  <p>{message}</p>
                  <button
                    onClick={() => {
                      dispatch(closeModal());
                      setTimeout(() => navigate("/dashboard"), 100);
                    }}
                    className="rounded mx-auto w-1/2 text-sm px-4 lg:px-5 py-2 lg:py-2.5 cta-one mt-4 font-semibold"
                  >
                    Close
                  </button>
                </div>
              ) : modalInitState.contentId === "already submitted" ? (
                <div className="flex flex-col space-y-4">
                  <p>Bank details have already been submitted.</p>
                </div>
              ) : modalInitState.contentId === "submission success" ? (
                <div className="flex flex-col space-y-4">
                  <p>Bank details have been submitted successfully.</p>
                </div>
              ) : modalInitState.contentId === "confirmSubmission" ? (
                <div className="flex flex-col space-y-4">
                  <p>
                    Please review your bank details carefully before submitting.
                    Once saved, they cannot be changed later.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleConfirmSubmission}
                      className="rounded w-1/3 text-sm px-4 py-2 cta-one font-semibold"
                      disabled={isLoading}
                    >
                      OK
                    </button>
                    <button
                      onClick={() => {
                        setPendingFormData(null);
                        dispatch(closeModal());
                      }}
                      className="rounded w-1/3 text-sm px-4 py-2 bg-gray-300 text-gray-700 font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </ModalComponent>
        )}

        {isLoading && <Loader />}
      </div>
    </>
  );
};

export default BankDetails;
